// ============================================
// story-fetch.js — resilient Home.json loader
// ============================================
//
// The compiled Ink story (story/Home.json) is fetched at four points: boot,
// quickstart (main.js), new-game (character-editor.js) and slot-load
// (save-load-ui.js). A single failed fetch dead-ends the player at a
// "FAILED TO LOAD STORY" wall with no recovery.
//
// Real-world failures are transient and client/edge-side, not packaging bugs
// (the file ships in every build): itch.io's BunnyCDN edge occasionally 403s a
// request, privacy extensions / corporate NSFW proxies can reject the
// `?v=<timestamp>` cache-buster, and mobile networks drop connections mid-load.
// foundby (intake) 0.2.7.3 Free: "FAILED TO LOAD STORY / HTTP 403" — single
// 403 on story/Home.json while every other asset loaded fine.
//
// Strategy: up to 3 attempts with short backoff. Attempt 1 keeps the
// `?v=` cache-buster (normal path, always-fresh JSON). Retries DROP the
// cache-buster — a bare `story/Home.json` sidesteps any edge/filter that
// rejects the timestamp query, and serving a slightly-stale-but-cached copy
// beats a hard dead-end.

const STORY_URL = 'story/Home.json';
const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [400, 1200]; // delay BEFORE attempt 2, then attempt 3

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Strip a `?v=<timestamp>` cache-buster param from a URL, leaving any other
 * query params intact. Used on retries so a bare URL can dodge edge/proxy/
 * extension filters that reject the timestamp query (same failure class as
 * fetchStoryJson's bare-URL retry — see header comment). A no-op for URLs that
 * never had a cache-buster (bare tar / manifest fetches).
 * @param {string} url
 * @returns {string}
 */
function _dropCacheBuster(url) {
    const qIdx = url.indexOf('?');
    if (qIdx === -1) return url;
    const base = url.slice(0, qIdx);
    const params = url.slice(qIdx + 1).split('&').filter((p) => p && !/^v=\d+$/.test(p));
    return params.length ? `${base}?${params.join('&')}` : base;
}

/**
 * Fetch the compiled Ink story JSON with retry + cache-buster fallback.
 * @returns {Promise<string>} the story JSON as text
 * @throws {Error} after all attempts fail, with the last status/cause
 */
export async function fetchStoryJson() {
    let lastErr = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        // Attempt 1 cache-busts for freshness; retries drop it to dodge
        // edge/proxy/extension rejection of the timestamp query.
        const url = attempt === 1 ? `${STORY_URL}?v=${Date.now()}` : STORY_URL;
        try {
            const resp = await fetch(url, { cache: attempt === 1 ? 'default' : 'reload' });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            return await resp.text();
        } catch (e) {
            lastErr = e;
            console.warn(`[PnO] Story load attempt ${attempt}/${MAX_ATTEMPTS} failed: ${e?.message || e}`);
            if (attempt < MAX_ATTEMPTS) await _sleep(BACKOFF_MS[attempt - 1] || 1200);
        }
    }
    throw new Error(`Failed to load story after ${MAX_ATTEMPTS} attempts: ${lastErr?.message || lastErr}`);
}

/**
 * Generic edge-403-resilient fetch for bare asset URLs (no cache-buster).
 * Same CDN-edge failure class as fetchStoryJson — itch.io's BunnyCDN edge
 * intermittently 403s/drops a request that succeeds on retry. Used by the
 * image-tar download and the image manifest, which previously did a single
 * bare fetch() and hard-failed (blank images) on a transient edge 403.
 *
 * Retries force `cache: 'reload'` so a negatively-cached edge response isn't
 * replayed, AND drop any `?v=<timestamp>` cache-buster — a bare URL sidesteps
 * the edge/proxy/extension filters that reject the timestamp query (the same
 * fallback fetchStoryJson uses). Without this, callers that bake in
 * `?v=Date.now()` (en.tsv, items.json) retry the rejected URL 3× and hard-fail:
 * the game then shows raw L10n keys for NPCs/buttons and can't load the item DB
 * (no equip/buy/clothing). foundby anon 0.2.9.0.4 Free, 2026-06-30. Returns the
 * raw Response so the caller can stream the body (tar) or parse JSON (manifest).
 * @returns {Promise<Response>} the first OK response
 * @throws {Error} after all attempts fail
 */
export async function fetchWithEdgeRetry(url, opts = {}) {
    const attempts = opts.attempts || MAX_ATTEMPTS;
    let lastErr = null;
    for (let attempt = 1; attempt <= attempts; attempt++) {
        // Attempt 1 keeps the cache-buster (always-fresh); retries drop it to
        // dodge edge/proxy/extension rejection of the `?v=` timestamp query.
        const attemptUrl = attempt === 1 ? url : _dropCacheBuster(url);
        try {
            const resp = await fetch(attemptUrl, { cache: attempt === 1 ? 'default' : 'reload' });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            return resp;
        } catch (e) {
            lastErr = e;
            console.warn(`[PnO] Asset load attempt ${attempt}/${attempts} failed (${attemptUrl}): ${e?.message || e}`);
            if (attempt < attempts) await _sleep(BACKOFF_MS[attempt - 1] || 1200);
        }
    }
    throw new Error(`Failed to load ${url} after ${attempts} attempts: ${lastErr?.message || lastErr}`);
}

/**
 * Render an actionable boot/load-failure card into a container element.
 *
 * The dominant real-world cause of a load failure is client/CDN-side — the
 * itch.io BunnyCDN edge 403ing the request, an ad/content blocker or privacy
 * extension stripping it, or an in-app/embedded browser — NOT a packaging bug
 * (the assets ship in every build). So we show self-service recovery steps
 * instead of a raw "HTTP 403". Copy is hardcoded English on purpose: this can
 * fire when en.tsv itself failed to load, so the localization layer can't be
 * trusted here. Inline styles only, so the card renders even if the section
 * CSS didn't load. We deliberately do NOT suggest the Patreon/VPS build — that
 * only helps actual patrons. foundby (intake) 0.2.8.3 Free, 2026-06-16.
 * @param {HTMLElement} containerEl target element (its content is replaced)
 * @param {Error|string} err the underlying load error (shown as small detail)
 */
export function renderLoadFailureCard(containerEl, err) {
    if (!containerEl) return;
    containerEl.innerHTML = `
        <div style="max-width:540px;margin:24px auto;padding:18px 20px;text-align:left;background:rgba(20,12,20,0.88);border:1px solid #c2557a;border-radius:12px;color:#f3e6ee;font-size:15px;line-height:1.55;">
            <div style="color:#f87171;font-weight:700;font-size:1.1em;margin-bottom:10px;">Couldn't load the game</div>
            <div style="margin-bottom:10px;">The game files couldn't be downloaded. This is almost always a network or browser issue, not a problem with your progress. Try:</div>
            <ul style="margin:0 0 12px 18px;padding:0;">
                <li>Reload the page, holding <b>Shift</b> for a hard refresh.</li>
                <li>Turn off ad / content blockers and privacy extensions for this page, then reload.</li>
                <li>Open the game in a normal browser tab (some in-app or embedded browsers block it).</li>
                <li>Check your internet connection, then try again.</li>
            </ul>
            <div class="load-fail-detail" style="opacity:0.5;font-size:0.78em;word-break:break-word;"></div>
        </div>`;
    const d = containerEl.querySelector('.load-fail-detail');
    if (d) d.textContent = `Details: ${err?.message || err || 'unknown error'}`;
}
